import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Collapse,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  HealthAndSafety as HealthIcon,
  ArrowForward as ArrowForwardIcon,
  VerifiedUser as VerifiedIcon,
  LocalHospital as HospitalIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { RootState, AppDispatch } from '../../store';
import { login } from '../../store/slices/authSlice';
import { clearAuthStorage } from '../../utils/clearStorage';

const validationSchema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

// Demo credentials - Only for development environment
// SECURITY: Remove or disable in production
const DEMO_CREDENTIALS = import.meta.env.DEV
  ? [
      { role: 'Admin', username: 'admin@civilhealth.gov.in', password: 'qwerty123', color: '#8B5CF6', icon: '⚙️' },
      { role: 'Coordinator', username: 'coordinator@civilhealth.gov.in', password: 'qwerty123', color: '#EC4899', icon: '👤' },
      { role: 'Doctor', username: 'doctor@civilhealth.gov.in', password: 'qwerty123', color: '#10B981', icon: '👨‍⚕️' },
      { role: 'IAS Officer', username: 'ias@gov.in', password: 'qwerty123', color: '#F59E0B', icon: '🏛️' },
      { role: 'IPS Officer', username: 'ips@gov.in', password: 'qwerty123', color: '#EF4444', icon: '🛡️' },
      { role: 'Senior Gov Officer', username: 'sgo@gov.in', password: 'qwerty123', color: '#06B6D4', icon: '📋' },
      { role: 'LokBhavan Staff', username: 'lokbhavan_staff@civilhealth.gov.in', password: 'qwerty123', color: '#14B8A6', icon: '👔' },
      { role: 'Office Admin', username: 'office_admin@civilhealth.gov.in', password: 'qwerty123', color: '#6366F1', icon: '🏢' },
      { role: 'Lab Admin', username: 'lab_admin@civilhealth.gov.in', password: 'qwerty123', color: '#0EA5E9', icon: '🧪' },
    ]
  : [];

const LoginPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const { isLoading, error } = useSelector((state: RootState) => state.auth);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState<number | null>(null);

  const formik = useFormik({
    initialValues: { username: '', password: '' },
    validationSchema,
    onSubmit: async (values) => {
      try {
        clearAuthStorage();
        await dispatch(login(values) as any).unwrap();
        navigate('/dashboard');
      } catch (err) {
        // Error handled by slice
      }
    },
  });

  const handleDemoLogin = (cred: typeof DEMO_CREDENTIALS[0]) => {
    clearAuthStorage();
    formik.setValues({ username: cred.username, password: cred.password });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #E8E8E8 0%, #F5F5F5 50%, #EBEBEB 100%)',
        position: 'relative',
      }}
    >
      {/* Decorative Background - Fixed position */}
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        {/* Gradient Orbs */}
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            right: '-5%',
            width: '40%',
            height: '40%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10%',
            left: '-5%',
            width: '40%',
            height: '40%',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '30%',
            left: '30%',
            width: '30%',
            height: '30%',
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
      </Box>

      {/* Main Content Area - Takes available space */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2, md: 4 },
          position: 'relative',
          zIndex: 1,
        }}
      >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{ width: '100%', maxWidth: 1200 }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            borderRadius: { xs: 3, sm: 5 },
            overflow: 'hidden',
            minHeight: { xs: 'auto', lg: 650 },
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Left Side - Branding */}
          <Box
            sx={{
              width: { xs: '100%', lg: '50%' },
              p: { xs: 2, sm: 3, md: 4, lg: 6 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              background: 'linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 50%, #FFFBEB 100%)',
              borderRight: { xs: 'none', lg: '1px solid #E5E7EB' },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* Logo */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, lg: 5 } }}>
                  <Box
                    sx={{
                      width: { xs: 64, sm: 76, lg: 88 },
                      height: { xs: 64, sm: 76, lg: 88 },
                      borderRadius: { xs: 3, lg: 4 },
                      background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #F59E0B 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: { xs: 2, sm: 2.5, lg: 3 },
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                    }}
                  >
                    <HealthIcon sx={{ fontSize: { xs: 32, sm: 38, lg: 44 }, color: 'white' }} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1.75rem', sm: '2rem', lg: '2.5rem' },
                      background: 'linear-gradient(135deg, #1A1A2E 0%, #3B82F6 50%, #8B5CF6 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Civilhealth
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#F59E0B',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Healthcare System
                  </Typography>
                  <Typography sx={{ mt: 1, color: '#6B7280', fontSize: '0.95rem' }}>
                    Executive Healthcare Management
                  </Typography>
                </Box>
              </motion.div>

              {/* Features */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {[
                  { icon: <VerifiedIcon sx={{ fontSize: 24 }} />, title: 'Secure Access', desc: 'Enterprise-grade security', color: '#3B82F6' },
                  { icon: <HospitalIcon sx={{ fontSize: 24 }} />, title: 'Healthcare Network', desc: 'Government hospitals', color: '#10B981' },
                  { icon: <SpeedIcon sx={{ fontSize: 24 }} />, title: 'Fast Response', desc: 'Real-time updates', color: '#F59E0B' },
                  { icon: <SupportIcon sx={{ fontSize: 24 }} />, title: '24×7 Support', desc: 'Dedicated assistance', color: '#8B5CF6' },
                ].map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1.5, sm: 2, lg: 2.5 },
                        p: { xs: 1.5, sm: 2, lg: 2.5 },
                        borderRadius: { xs: 2, sm: 2.5, lg: 3 },
                        background: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                          transform: 'translateX(8px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 40, sm: 44, lg: 50 },
                          height: { xs: 40, sm: 44, lg: 50 },
                          borderRadius: { xs: 2, sm: 2, lg: 2.5 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: `${feature.color}15`,
                          color: feature.color,
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#1A1A2E', fontWeight: 600, mb: 0.5, fontSize: '0.95rem' }}>
                          {feature.title}
                        </Typography>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>
                          {feature.desc}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </Box>

              {/* Trust Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
              >
                <Box
                  sx={{
                    mt: { xs: 2, sm: 3, lg: 4 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    borderRadius: { xs: 2, sm: 3 },
                    background: 'linear-gradient(135deg, #FFFBEB 0%, #ECFDF5 100%)',
                    border: '1px solid #FCD34D',
                  }}
                >
                  <VerifiedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#F59E0B' }} />
                  <Typography sx={{ color: '#374151', fontSize: { xs: '0.75rem', sm: '0.85rem' }, fontWeight: 500 }}>
                    Government of India • ISO 27001 Certified
                  </Typography>
                </Box>
              </motion.div>
            </Box>
          </Box>

          {/* Right Side - Login Form */}
          <Box
            sx={{
              width: { xs: '100%', lg: '50%' },
              p: { xs: 2, sm: 3, md: 4, lg: 6 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 440, mx: 'auto', width: '100%', px: { xs: 0, sm: 0 } }}>
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1.5rem', sm: '1.75rem', lg: '2rem' },
                      color: '#1A1A2E',
                      mb: 1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Welcome Back
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Sign in to your healthcare dashboard
                  </Typography>
                </Box>
              </motion.div>

              {/* Error Alert */}
              <Collapse in={!!error}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2.5,
                    bgcolor: 'transparent' ,
                    border: '1px solid #FCA5A5',
                    color: '#DC2626',
                    '& .MuiAlert-icon': { color: '#EF4444' },
                  }}
                >
                  {error}
                </Alert>
              </Collapse>

              {/* Login Form */}
              <form onSubmit={formik.handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Username Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <Typography sx={{ color: '#374151', mb: 1.5, fontWeight: 500, fontSize: '0.9rem' }}>
                      Username
                    </Typography>
                    <TextField
                      fullWidth
                      id="username"
                      name="username"
                      placeholder="Enter your username"
                      value={formik.values.username}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.username && Boolean(formik.errors.username)}
                      helperText={formik.touched.username && formik.errors.username}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          bgcolor: 'transparent' ,
                          transition: 'all 0.3s ease',
                          '& fieldset': { borderColor: '#E5E7EB' },
                          '&:hover': {
                            bgcolor: 'transparent' ,
                            '& fieldset': { borderColor: '#3B82F6' },
                          },
                          '&.Mui-focused': {
                            bgcolor: 'transparent' ,
                            boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
                            '& fieldset': { borderColor: '#3B82F6' },
                          },
                        },
                        '& .MuiFormHelperText-root': { color: '#DC2626' },
                      }}
                    />
                  </motion.div>

                  {/* Password Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                  >
                    <Typography sx={{ color: '#374151', mb: 1.5, fontWeight: 500, fontSize: '0.9rem' }}>
                      Password
                    </Typography>
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ color: '#9CA3AF' }}
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          bgcolor: 'transparent' ,
                          transition: 'all 0.3s ease',
                          '& fieldset': { borderColor: '#E5E7EB' },
                          '&:hover': {
                            bgcolor: 'transparent' ,
                            '& fieldset': { borderColor: '#3B82F6' },
                          },
                          '&.Mui-focused': {
                            bgcolor: 'transparent' ,
                            boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
                            '& fieldset': { borderColor: '#3B82F6' },
                          },
                        },
                        '& .MuiFormHelperText-root': { color: '#DC2626' },
                      }}
                    />
                  </motion.div>

                  {/* Forgot Password */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography
                      sx={{
                        color: '#3B82F6',
                        cursor: 'pointer',
                        fontWeight: 500,
                        transition: 'color 0.2s ease',
                        '&:hover': { color: '#1D4ED8', textDecoration: 'underline' },
                      }}
                      onClick={() => navigate('/forgot-password')}
                    >
                      Forgot Password?
                    </Typography>
                  </Box>

                  {/* Login Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                  >
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        py: 2,
                        borderRadius: 2.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '1rem',
                        background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          background: '#E5E7EB',
                          boxShadow: 'none',
                          transform: 'none',
                        },
                      }}
                    >
                      {isLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CircularProgress size={20} color="inherit" />
                          <span>Signing in...</span>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          Sign In
                          <ArrowForwardIcon sx={{ fontSize: 18 }} />
                        </Box>
                      )}
                    </Button>
                  </motion.div>
                </Box>
              </form>

              {/* Divider */}
              <Divider sx={{ my: 4, borderColor: '#E5E7EB' }}>
                <Chip
                  label="Quick Access"
                  size="small"
                  sx={{
                    bgcolor: 'transparent' ,
                    color: '#D97706',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    border: '1px solid #FCD34D',
                  }}
                />
              </Divider>

              {/* Demo Credentials */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.4 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {DEMO_CREDENTIALS.map((cred, index) => (
                    <motion.div
                      key={cred.role}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + index * 0.1, duration: 0.3 }}
                    >
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setSelectedDemo(index);
                          handleDemoLogin(cred);
                        }}
                        sx={{
                          py: 1.5,
                          px: 2.5,
                          borderRadius: 2.5,
                          textTransform: 'none',
                          borderColor: selectedDemo === index ? cred.color : '#E5E7EB',
                          bgcolor: selectedDemo === index ? `${cred.color}10` : 'transparent',
                          color: selectedDemo === index ? cred.color : '#374151',
                          fontWeight: selectedDemo === index ? 600 : 500,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: cred.color,
                            bgcolor: `${cred.color}10`,
                            color: cred.color,
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="body1">{cred.icon}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>{cred.role}</Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6B7280',
                              fontFamily: 'monospace',
                              bgcolor: 'transparent' ,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {cred.username}
                          </Typography>
                        </Box>
                      </Button>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>

              {/* Register Link */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
              >
                <Box sx={{ textAlign: 'center', mt: 4, px: { xs: 1, sm: 0 } }}>
                  <Typography
                    sx={{
                      color: '#6B7280',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      lineHeight: { xs: 1.6, sm: 1.5 },
                      display: { xs: 'block', sm: 'inline' },
                    }}
                  >
                    Don't have an account?
                  </Typography>
                  <Box sx={{ display: { xs: 'block', sm: 'inline' } }}>
                    {' '}
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => navigate('/register')}
                      sx={{
                        color: '#3B82F6',
                        textTransform: 'none',
                        fontWeight: 600,
                        p: 0,
                        minWidth: 'auto',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        mt: { xs: 0.5, sm: 0 },
                        '&:hover': {
                          background: 'transparent',
                          color: '#1D4ED8',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Register here
                    </Button>
                  </Box>
                </Box>
              </motion.div>
            </Box>
          </Box>
        </Paper>
      </motion.div>
      </Box>

      {/* Footer - Normal document flow, no absolute positioning */}
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          textAlign: 'center',
          background: 'linear-gradient(0deg, rgba(245, 245, 245, 0.9) 0%, transparent 100%)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#6B7280',
            display: 'block',
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            lineHeight: 1.5,
          }}
        >
          © 2026 Civilhealth Healthcare Response Management System
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: '#6B7280',
            display: 'block',
            fontSize: { xs: '0.65rem', sm: '0.7rem' },
            mt: 0.5,
          }}
        >
          A Government of India Initiative
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;